import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Upload, Download, FileWarning, Loader2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { toast } from "components/ui/use-toast";

import {
  useUploadSegmentationMutation,
  useValidateSegmentationMutation,
} from "../../../../../services/gamification";
import { ISegmentationResult } from "../../../../../@types/gamification";
import { SEGMENTATION_COPY } from "../../constants/messages";
import { type UpdateGamificationFormData } from "../../validations/gamification.validation";
import { usePendingSegmentation } from "../hooks/usePendingSegmentation";
import { CpfSegmentationList } from "../../components/cpf-segmentation-list";
import {
  downloadCpfTemplate,
  toImportableCpfFile,
} from "../../utils/cpf-spreadsheet";
import { buildCpfCsvFile, dedupeCpfs } from "../../utils/segmentation";

const SEGMENTATION_ACCEPT =
  ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function SegmentationCard({ campaignId }: { campaignId: number }) {
  const form = useFormContext<UpdateGamificationFormData>();
  form.register("segmentationCpfs");
  const watchedCpfs: string[] | undefined = useWatch({
    control: form.control,
    name: "segmentationCpfs",
  });
  const cpfs: string[] = watchedCpfs ?? [];
  const hasPendingBase: boolean = usePendingSegmentation(form);
  const [report, setReport] = useState<ISegmentationResult | null>(null);
  const [validateSegmentation, { isLoading: isValidating }] =
    useValidateSegmentationMutation();
  const [upload, { isLoading: isUploading }] = useUploadSegmentationMutation();

  const setCpfs = (next: string[]): void => {
    form.setValue("segmentationCpfs", next.length > 0 ? next : undefined, {
      shouldDirty: true,
    });
  };

  const handleImport = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const importable = await toImportableCpfFile(file);
      const result = await validateSegmentation({ file: importable }).unwrap();
      setReport(result);
      const latest: string[] = form.getValues("segmentationCpfs") ?? [];
      setCpfs(dedupeCpfs([...latest, ...result.validCpfs]));
    } catch {
      toast({
        title: "Erro",
        description: "Falha ao ler a base de CPFs. Tente novamente.",
        variant: "destructive",
      });
    }
    event.target.value = "";
  };

  const handleSave = async () => {
    if (cpfs.length === 0) return;
    try {
      const res = await upload({
        campaignId,
        file: buildCpfCsvFile(cpfs),
      }).unwrap();
      form.resetField("segmentationCpfs", { defaultValue: cpfs });
      toast({
        title: "Base gravada",
        description: `${res.valid} CPF(s) válido(s), ${res.invalid} inválido(s).`,
      });
    } catch {
      toast({
        title: "Erro",
        description: "Falha ao gravar a base de CPFs.",
        variant: "destructive",
      });
    }
  };

  const downloadInvalid = () => {
    if (!report?.invalidRows?.length) return;
    const header = "linha,valor,motivo";
    const lines = report.invalidRows.map(
      (r) => `${r.line},"${r.value}","${r.reason}"`,
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cpfs-invalidos-${campaignId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Segmentação de CPF</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">
          Importe um arquivo CSV/XLSX com os CPFs elegíveis ou monte a base à
          mão. Baixe o modelo em Excel se quiser começar do formato certo — os
          CPFs de exemplo devem ser apagados. Revise, edite e remova CPFs antes
          de gravar. A base gravada substitui a anterior; sem base, a campanha
          fica aberta a todos.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="segmentation-file">
              Importar arquivo (CSV ou XLSX)
            </Label>
            <Input
              id="segmentation-file"
              type="file"
              accept={SEGMENTATION_ACCEPT}
              onChange={handleImport}
              disabled={isValidating || isUploading}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => downloadCpfTemplate()}
          >
            <Download className="mr-2 h-4 w-4" />
            Baixar modelo (Excel)
          </Button>
        </div>

        {isValidating && (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Lendo a base de CPFs...
          </p>
        )}

        <CpfSegmentationList
          cpfs={cpfs}
          onChange={setCpfs}
          disabled={isValidating || isUploading}
        />

        {hasPendingBase && (
          <p role="status" className="text-sm font-medium text-amber-700">
            {SEGMENTATION_COPY.pendingNotice}
          </p>
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={cpfs.length === 0 || isUploading}
          >
            <Upload className="mr-2 h-4 w-4" />
            {isUploading ? "Gravando..." : "Gravar base"}
          </Button>
        </div>

        {report && (
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <span>
                Último import: <strong>{report.total}</strong> linha(s)
              </span>
              <span className="text-green-700">
                Válidos: <strong>{report.valid}</strong>
              </span>
              <span className="text-red-600">
                Inválidos: <strong>{report.invalid}</strong>
              </span>
            </div>

            {report.invalidRows.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-2 text-sm font-medium text-amber-700">
                    <FileWarning className="h-4 w-4" />
                    Linhas inválidas
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadInvalid}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Baixar CSV
                  </Button>
                </div>
                <div className="max-h-56 overflow-auto rounded border">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="px-3 py-2">Linha</th>
                        <th className="px-3 py-2">Valor</th>
                        <th className="px-3 py-2">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.invalidRows.map((row, index) => (
                        <tr key={`${row.line}-${index}`} className="border-t">
                          <td className="px-3 py-2">{row.line}</td>
                          <td className="px-3 py-2">{row.value}</td>
                          <td className="px-3 py-2">{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
